import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch, useSelector } from 'react-redux'
import { getFormInitialValues, initialize, reset } from 'redux-form'
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import cx from 'classnames'
import i18next from 'i18next'
import { useNavigate } from 'react-router-dom'
import { get, isEmpty } from 'lodash'
import { Button, Checkbox, Col, Dropdown, Popover, Row, TablePaginationConfig, Tooltip } from 'antd'
import Table, { ColumnsType } from 'antd/lib/table'
import { MenuProps } from 'antd/lib'

// reducers
import { SorterResult } from 'antd/lib/table/interface'
import { RootState } from '../../reducers'
import { getFormDocs, IFormDocDateChange, IFormDocTableItem, IQueue } from '../../reducers/formDocs/formDocsActions'
import { getDatabases, getQueues } from '../../reducers/form/formActions'

// utils
import { EDITING_TIME_MINUTES_LIMIT, FORM, FORM_DOC_ORDER, FORM_DOC_STATUS, PAGINATION } from '../../utils/enums'
import { orderDirectionByTable, orderDirectionForTable } from '../../utils/helper'
import { postReq } from '../../utils/request'

// assets
import { ReactComponent as FilterIconGrey } from '../../assets/icons/BaseMiniIcons/filter_grey.svg'
import { ReactComponent as FilterIconOrange } from '../../assets/icons/BaseMiniIcons/filter_orange.svg'
import { ReactComponent as CopyIcon } from '../../assets/icons/BaseIcons/icon-copy.svg'
import { ReactComponent as CopyLinkIcon } from '../../assets/icons/BaseIcons/copy-icon.svg'
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/BaseMiniIcons/chevron-down-3-icon.svg'

// components
import FormDocsPageFilter from './components/FormDocsPageFilter'
import CreateFormModal from '../../components/modals/CreateFormModal'
import FormSettingsModal from '../../components/modals/FormSettingsModal'

// types
import { IFormSettingsModal, IUpdateForm } from '../../types/interfaces'

type Columns = ColumnsType<IFormDocTableItem>

const statuses: MenuProps['items'] = [
	{
		label: i18next.t('loc:Production'),
		key: FORM_DOC_STATUS.PRODUCTION
	},
	{
		label: i18next.t('loc:In progress'),
		key: FORM_DOC_STATUS.IN_PROGRESS
	},
	{
		label: i18next.t('loc:Archived'),
		key: FORM_DOC_STATUS.ARCHIVED
	}
]

const defaultQueryStatus: string[] = [FORM_DOC_STATUS.IN_PROGRESS, FORM_DOC_STATUS.PRODUCTION]

const FormDocsPage = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [isCreateFormModalShowed, setCreateFormModalShowed] = useState(false)
	const [isFormModalShowed, setFormModalShowed] = useState(false)
	const [templateId, setTemplateId] = useState<number>(-1)
	const [modalTitle, setModalTitle] = useState<string>('')
	const loginUserData = useSelector((state: RootState) => state.auth)

	const renderDateWithAuthor = (data: IFormDocDateChange, nowEdited?: boolean) => {
		if (data && !nowEdited) {
			return (
				<>
					<span className={'text-gray-700 inline-block'}>{data.date}</span>
					<span className={'text-gray-300 mx-1'}>|</span>
					<span className={'text-gray-700'}>{data?.author || '-'}</span>
				</>
			)
		}
		return (
			<>
				<span className={'text-gray-700 inline-block'}>{t('loc:Now edited')}</span>
				<span className={'text-gray-300 mx-1'}>|</span>
				<span className={'text-gray-700'}>{data?.author || '-'}</span>
			</>
		)
	}

	const backUrl = btoa(`${window.location.pathname}${window.location.search}`)
	const filterInitialValues = useSelector((state: RootState) => getFormInitialValues(FORM.FORM_DOCS_PAGE_FILTER)(state))

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		type: StringParam,
		usage: StringParam,
		limit: NumberParam,
		statusFilter: withDefault(ArrayParam, defaultQueryStatus),
		orderBy: withDefault(StringParam, undefined),
		orderDirection: withDefault(StringParam, 'asc'),
		page: withDefault(NumberParam, 1)
	})

	const formDocs = useSelector((state: RootState) => state.formDoc.formDocs)
	const loading = formDocs?.isLoading

	useEffect(() => {
		dispatch(
			getFormDocs(query.page, query.limit, {
				search: query.search,
				orderBy: query.orderBy,
				orderDirection: query.orderDirection,
				statusFilter: query.statusFilter
			})
		)
	}, [dispatch, query.page, query.limit, query.search, query.orderBy, query.orderDirection, query.statusFilter])

	useEffect(() => {
		if (!filterInitialValues || isEmpty(filterInitialValues)) {
			const initData = {
				search: query.search || null
			}
			dispatch(initialize(FORM.FORM_DOCS_PAGE_FILTER, initData))
		}
	}, [dispatch, filterInitialValues, query.search])

	const duplicateFormDocs = async (formId: number, record: IFormDocTableItem) => {
		try {
			const duplicatedFormName = `${record.name}_${uuidv4()}`
			const data = await postReq(`/api/admin/forms/${formId}/duplicate/`, null, { formName: duplicatedFormName })
			const newFormId = data.data.data?.newFormID
			navigate(`${t('paths:form-builder/{{templateId}}', { templateId: newFormId }) as string}?backUrl=${backUrl}`)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const filterStatuses = (values: any) => {
		setQuery({
			...query,
			statusFilter: [...values],
			page: 1
		})
	}

	function mapFormDocStatus(s: FORM_DOC_STATUS) {
		switch (s) {
			case FORM_DOC_STATUS.IN_PROGRESS:
				return t('loc:In progress')
			case FORM_DOC_STATUS.PRODUCTION:
				return t('loc:Production')
			case FORM_DOC_STATUS.ARCHIVED:
				return t('loc:Archived')
			default:
				return ''
		}
	}

	const FilterCheckbox = (props: { filter: FORM_DOC_STATUS }) => {
		const { filter } = props
		return (
			<Row>
				<Checkbox className={'px-2 py-1'} value={filter}>
					{mapFormDocStatus(filter)}
				</Checkbox>
			</Row>
		)
	}

	const checkIfFormIsEditing = (form: IFormDocTableItem) => {
		const time = Math.abs(dayjs(form?.updatedAt || 0).diff(dayjs(), 'minutes'))
		if (`${form?.lastEdit?.id}` === loginUserData.authUser?.data?.id || time >= EDITING_TIME_MINUTES_LIMIT || form?.editor === null) {
			return false
		}
		return true
	}

	const changeStatus = async (event: any, formId: number) => {
		try {
			await postReq(`/api/admin/forms/${formId}/status`, undefined, { status: event.key })
			dispatch(
				getFormDocs(query.page, query.limit, {
					search: query.search,
					orderBy: query.orderBy,
					orderDirection: query.orderDirection,
					statusFilter: query.statusFilter
				})
			)
		} catch (err: any) {
			// eslint-disable-next-line no-console
			console.error(err.message)
		}
	}

	const columns: Columns = [
		{
			title: t('loc:ID'),
			width: '5rem',
			ellipsis: {
				showTitle: false
			},
			key: 'id',
			align: 'right',
			dataIndex: 'id',
			render(value) {
				return <span className={'text-gray-700'}>{value || '-'}</span>
			}
		},
		{
			title: t('loc:Názov'),
			ellipsis: {
				showTitle: false
			},
			key: 'name',
			sorter: true,
			sortOrder: query.orderBy === FORM_DOC_ORDER.NAME ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			dataIndex: 'name',
			render(value, record) {
				return (
					<span
						className={'text-gray-800 font-medium title-form-list'}
						onClick={() => {
							navigate(`${t('paths:form-builder/{{templateId}}', { templateId: record.id }) as string}?backUrl=${backUrl}`)
						}}
					>
						{value || '-'}
					</span>
				)
			}
		},
		{
			title: t('loc:Daktela'),
			ellipsis: {
				showTitle: false
			},
			key: 'queue',
			dataIndex: 'queue',
			sorter: true,
			sortOrder: query.orderBy === FORM_DOC_ORDER.QUEUE ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			render(value: IQueue) {
				return <span className={'text-gray-700'}>{value?.title || '-'}</span>
			}
		},
		{
			title: t('loc:Status'),
			ellipsis: {
				showTitle: false
			},
			key: 'status',
			dataIndex: 'status',
			// eslint-disable-next-line react/display-name
			filterDropdown: () => (
				<Col className={'px-2 py-2'}>
					<Checkbox.Group
						className={'table-form-status-filter'}
						style={{ width: '100%' }}
						onChange={filterStatuses}
						value={query?.statusFilter as string[]}
					>
						<FilterCheckbox filter={FORM_DOC_STATUS.PRODUCTION} />
						<FilterCheckbox filter={FORM_DOC_STATUS.IN_PROGRESS} />
						<FilterCheckbox filter={FORM_DOC_STATUS.ARCHIVED} />
					</Checkbox.Group>
				</Col>
			),
			filterMultiple: true,
			// eslint-disable-next-line react/display-name
			filterIcon: () => {
				const style = {
					maxWidth: '100%',
					width: '100%'
				}
				return (
					<div className={'w-full h-full grid justify-items-center flex items-center'}>
						{query?.statusFilter ? <FilterIconOrange style={style} className={'w-full max-w-full'} /> : <FilterIconGrey style={style} />}
					</div>
				)
			},
			onFilter: (value, record) => typeof value === 'string' && record.status.includes(value),
			render(value, record) {
				const status = value.toUpperCase()
				const parsedStatus = `${status?.charAt(0)?.toUpperCase()}${status.replace('_', ' ').toLocaleLowerCase().slice(1)}`
				const editClass = cx({
					'in-progress-select': status === FORM_DOC_STATUS.IN_PROGRESS,
					'production-select': status === FORM_DOC_STATUS.PRODUCTION,
					'archived-select': status === FORM_DOC_STATUS.ARCHIVED
				})
				return (
					<Dropdown
						className={editClass}
						menu={{ items: statuses, onClick: (event) => changeStatus(event, record?.id) }}
						disabled={checkIfFormIsEditing(record)}
					>
						<Button>
							{parsedStatus} <ChevronDownIcon />
						</Button>
					</Dropdown>
				)
			}
		},
		// 	{
		// 	title: t('loc:State'),
		// 	ellipsis: {
		// 		showTitle: false
		// 	},
		// 	width: '6rem',
		// 	key: 'state',
		// 	dataIndex: 'state',
		// 	align: 'center',
		// 	render(value) {
		// 		return value ? <CheckCircleTwoTone className={'text-xl'} twoToneColor={green['300']} /> : <ExclamationCircle />
		// 	}
		// },
		{
			title: t('loc:Created'),
			ellipsis: {
				showTitle: false
			},
			key: 'createdAt',
			dataIndex: 'created',
			sorter: true,
			sortOrder: query.orderBy === FORM_DOC_ORDER.CREATED_AT ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			render(value: IFormDocDateChange) {
				return renderDateWithAuthor(value) || '-'
			}
		},
		{
			title: t('loc:Last edit'),
			ellipsis: {
				showTitle: false
			},
			key: 'updatedAt',
			dataIndex: 'lastEdit',
			sorter: true,
			sortOrder: query.orderBy === FORM_DOC_ORDER.UPDATED_AT ? orderDirectionForTable(query.orderDirection as 'asc' | 'desc') : null,
			render(value: IFormDocDateChange, record) {
				return renderDateWithAuthor(value, checkIfFormIsEditing(record)) || '-'
			}
		},
		{
			title: '',
			ellipsis: {
				showTitle: false
			},
			width: '12rem',
			key: 'id',
			dataIndex: 'id',
			sorter: false,
			render(value, record) {
				const copyToClipboard = (textToClipboard: string) => {
					// noinspection JSIgnoredPromiseFromCall
					navigator.clipboard.writeText(textToClipboard)
					return textToClipboard
				}

				return (
					<div className={'links'}>
						<Button
							type='link'
							className={'wrap'}
							icon={<CopyIcon />}
							onClick={() => {
								duplicateFormDocs(value, record)
							}}
						/>
						<Popover
							overlayInnerStyle={{ padding: '0px', borderRadius: '6px' }}
							placement='left'
							content={
								<Tooltip
									placement='bottom'
									title={
										<div>
											<CopyLinkIcon />
											<span className={'ml-2'}>{t('loc:Link copied to clipboard')}</span>
										</div>
									}
								>
									<div className={'p-2'}>{record.iframeUrl}</div>
								</Tooltip>
							}
							trigger='click'
						>
							<Button
								onClick={() => {
									copyToClipboard(record.iframeUrl)
								}}
								className={'wrap ml-1'}
								type='link'
							>
								{t('loc:Copy link')}
							</Button>
						</Popover>
					</div>
				)
			}
		}
	]

	const onChangeTable = (pagination: TablePaginationConfig, sorter: SorterResult<any> | SorterResult<any>[]) => {
		if (!(sorter instanceof Array)) {
			let order: any = {
				orderBy: undefined,
				orderDirection: undefined
			}
			if (sorter?.order) {
				order = Array.isArray(sorter)
					? {
							orderBy: sorter[0].columnKey,
							orderDirection: orderDirectionByTable(sorter[0].order)
						}
					: {
							orderBy: sorter.columnKey,
							orderDirection: orderDirectionByTable(sorter.order)
						}
			}

			const newQuery = {
				...query,
				...order,
				limit: pagination?.pageSize,
				page: pagination?.current
			}
			setQuery(newQuery)
		}
	}

	const searchSubmit = useCallback(
		(value: object) => {
			const newQuery = {
				...query,
				search: get(value, 'search') || '',
				page: 1
			}
			setQuery(newQuery)
		},
		[query, setQuery]
	)

	const resetFilter = () => {
		const newQuery = {
			statusFilter: undefined,
			search: undefined,
			page: 1,
			type: undefined,
			usage: undefined,
			orderBy: undefined,
			orderDirection: undefined
		}
		setQuery(newQuery)
	}

	// open create form modal
	const openCreateFormModal = () => {
		setCreateFormModalShowed(true)
	}

	const createNewForm = async (formData: IFormSettingsModal) => {
		const newForm: IUpdateForm = {
			formName: formData.name,
			formDatabase: formData.database,
			formQueue: formData.queue,
			formType: formData.type
		}
		try {
			let form: any = {}
			// check if form is created from template or no
			if (templateId > 0) {
				// create form from template
				form = await postReq(`/api/admin/templates/${templateId}/apply`, undefined, newForm)
				setTemplateId(-1)
			} else {
				// create blank form
				form = await postReq('/api/admin/forms/', undefined, newForm)
			}
			setFormModalShowed(false)
			navigate(t(`paths:/form-builder/{{templateId}}?backUrl=${backUrl}`, { templateId: form?.data?.data?.formID }) as string)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			dispatch(reset(FORM.GENERALE_TEMPLATE_ATTRIBUTES))
		}
	}

	// create form from template
	const createFormFromTemplate = async (template: any) => {
		setModalTitle(t('loc:New from template') as string)
		dispatch(getQueues())
		dispatch(getDatabases())
		setFormModalShowed(true)
		setCreateFormModalShowed(false)
		const data = {
			queue: template?.formQueue?.name,
			database: template.formDatabase,
			type: template.formType
		}
		dispatch(initialize(FORM.GENERALE_TEMPLATE_ATTRIBUTES, data))
		setTemplateId(template.id)
	}

	return (
		<>
			<Row>
				<Col span={24}>
					<div className='content-wrapper'>
						<FormDocsPageFilter openCreateFormModal={openCreateFormModal} onSubmit={searchSubmit} resetFilter={resetFilter} />
						<Table
							className='table-fixed mainTable leading-none'
							// 21rem = height of header + height of filter
							style={{
								borderRadius: 25,
								minHeight: 'calc(100vh - 19rem)'
							}}
							tableLayout={'fixed'}
							rowClassName={'clickable-row'}
							onChange={(pagination, sorter) => onChangeTable(pagination, sorter)}
							columns={columns}
							dataSource={formDocs?.tableData}
							loading={loading}
							scroll={{ y: 'calc(100vh - 19rem)' }}
							pagination={{
								showTotal: (total, [from, to]) =>
									t('loc:{{from}} - {{to}} z {{total}}', {
										total,
										from,
										to
									}),
								position: ['bottomRight'],
								defaultPageSize: PAGINATION.defaultPageSize,
								pageSizeOptions: PAGINATION.pageSizeOptions,
								pageSize: formDocs?.pagination?.limit,
								total: formDocs?.pagination?.totalCount,
								current: formDocs?.pagination?.page
							}}
						/>
					</div>
				</Col>
			</Row>
			<CreateFormModal
				width={460}
				btnPushed={() => {
					setModalTitle(t('loc:New blank form') as string)
					dispatch(getQueues())
					dispatch(getDatabases())
					setFormModalShowed(true)
					setCreateFormModalShowed(false)
				}}
				isVisible={isCreateFormModalShowed}
				createFormFromTemplate={createFormFromTemplate}
				onCancel={() => {
					setCreateFormModalShowed(false)
				}}
			/>
			<FormSettingsModal
				width={460}
				handleSubmit={createNewForm}
				isVisible={isFormModalShowed}
				title={modalTitle}
				onCancel={() => {
					// reset template id
					setTemplateId(0)
					setFormModalShowed(false)
					dispatch(initialize(FORM.GENERALE_TEMPLATE_ATTRIBUTES, {}))
				}}
				cancelText={t('loc:Back')}
				onConfirmBtnTitle={t('loc:Create form')}
			/>
		</>
	)
}

export default FormDocsPage
